/**
 * The project a new chat will live in (07 Workshop, rows 1 and 73): the
 * brand's active projects, newest activity first, then New project. Used under
 * the new chat's composer and in Quick Create's pop-up. With no projects yet it
 * reads New project, and the first send starts one that M7 names.
 */
import { Button, Menu, MenuItem, MenuTrigger, Popover, type Key } from "react-aria-components";
import { Check, ChevronDown, Folder, FolderPlus } from "lucide-react";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { cn } from "@/utils/helpers";
import type { WorkshopProject } from "@/types/workshop.type";
import { useWorkshopProjects } from "../api/useWorkshopProjects";
import { sortProjects, useProjectSort } from "../workshopSort";
import { isArchived } from "./WorkshopParts";
import { useWorkshopDesk } from "./workshopDesk";

/** The brand's active projects in the person's order, and the one a chat lands in by default. */
export function usePickableProjects() {
  const desk = useWorkshopDesk();
  const { projects, isLoading } = useWorkshopProjects(desk.brandId);
  const [sort] = useProjectSort();
  const live = projects.filter((p) => !isArchived(p));
  return {
    active: sortProjects(live, sort),
    latest: sortProjects(live, "activity")[0] ?? null,
    isLoading,
  };
}

type Props = {
  /** The picked project; null with no projects yet (it reads New project). */
  project: WorkshopProject | null;
  projects: WorkshopProject[];
  onPick: (projectId: string) => void;
  canCreate: boolean;
  /** The projects are still loading: a quiet pill, never a flash of New project. */
  loading?: boolean;
  className?: string;
};

export default function WorkshopProjectPicker({ project, projects, onPick, canCreate, loading, className }: Props) {
  const desk = useWorkshopDesk();
  const portal = useCoShellPortal();
  const firstRun = projects.length === 0;

  if (loading) {
    return (
      <div className={cn("ws-picker ws-picker--static", className)} aria-busy="true" aria-label="Loading projects">
        <span className="ws-picker-skel" aria-hidden="true" />
      </div>
    );
  }

  // Nothing to pick and no New project row (Home): a plain label, since the
  // menu would open empty.
  if (firstRun && !canCreate) {
    return (
      <div className={cn("ws-picker ws-picker--static", className)}>
        <FolderPlus className="ws-picker-lead" aria-hidden="true" />
        <span className="ws-picker-value">New project</span>
      </div>
    );
  }

  const onAction = (key: Key) => {
    if (key === "__new") {
      desk.openNewProject(onPick);
      return;
    }
    onPick(String(key));
  };

  return (
    <MenuTrigger>
      <Button
        className={cn("ws-picker", className)}
        aria-label={`Project: ${firstRun ? "New project" : project?.name ?? ""}`}
      >
        {firstRun ? (
          <FolderPlus className="ws-picker-lead" aria-hidden="true" />
        ) : (
          <Folder className="ws-picker-lead" aria-hidden="true" />
        )}
        <span className="ws-picker-value">{firstRun ? "New project" : project?.name}</span>
        <ChevronDown className="ws-picker-chev" aria-hidden="true" />
      </Button>
      <Popover
        placement="bottom start"
        offset={6}
        UNSTABLE_portalContainer={portal ?? undefined}
        className="ws-menu-pop ws-menu-pop--picker"
      >
        <Menu aria-label="Where the chat lives" className="ws-menu-list" onAction={onAction}>
          {projects.map((p) => (
            <MenuItem
              key={p.id}
              id={p.id}
              textValue={p.name}
              className={cn("ws-menu-row", p.id === project?.id && "is-current")}
            >
              <Folder className="ws-menu-row-icon" aria-hidden="true" />
              <span className="ws-menu-row-label">{p.name}</span>
              {p.id === project?.id ? <Check className="ws-menu-row-check" aria-hidden="true" /> : null}
            </MenuItem>
          ))}
          {canCreate ? (
            <MenuItem id="__new" textValue="New project…" className="ws-menu-row">
              <FolderPlus className="ws-menu-row-icon" aria-hidden="true" />
              <span className="ws-menu-row-label">New project…</span>
            </MenuItem>
          ) : null}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
